// Unified Task Service
// Uses /api/v1/tasks endpoints (Firestore-based)

import { get, post, put, del, buildUrl } from '@/services/apiClient';
import {
  ITask,
  ITaskCreateRequest,
  ITaskUpdateRequest,
  ITaskMoveRequest,
  ITaskOrder,
  ITaskListResponse,
  ITaskByOwnerResponse,
  ITaskByProjectResponse,
  ITaskByPhaseResponse,
  ITaskStats,
  IDependencyCheck,
  ITaskFilter,
} from '@/types/task';

const BASE_URL = '/api/v1/tasks';

// ============================================================================
// Core CRUD
// ============================================================================

export const listTasks = async (filter?: ITaskFilter): Promise<ITaskListResponse> => {
  const params: Record<string, string> = {};

  if (filter?.project_id) params.project_id = filter.project_id;
  if (filter?.phase_id) params.phase_id = filter.phase_id;
  if (filter?.task_owner) params.task_owner = filter.task_owner;
  if (filter?.status) params.status = filter.status;
  if (filter?.statuses) params.statuses = filter.statuses.join(',');
  if (filter?.priority) params.priority = filter.priority;
  if (filter?.tags) params.tags = filter.tags.join(',');
  if (filter?.include_completed === false) params.include_completed = 'false';
  if (filter?.grouped) params.grouped = 'true';
  if (filter?.limit) params.limit = filter.limit.toString();
  if (filter?.offset) params.offset = filter.offset.toString();

  const url = buildUrl(BASE_URL, params);
  return get<ITaskListResponse>(url);
};

export const getTask = async (id: string): Promise<ITask> => {
  return get<ITask>(`${BASE_URL}/${id}`);
};

export const createTask = async (data: ITaskCreateRequest): Promise<ITask> => {
  return post<ITask>(BASE_URL, data);
};

export const updateTask = async (id: string, data: ITaskUpdateRequest): Promise<ITask> => {
  return put<ITask>(`${BASE_URL}/${id}`, data);
};

export const deleteTask = async (id: string): Promise<void> => {
  await del(`${BASE_URL}/${id}`);
};

// ============================================================================
// Task Operations
// ============================================================================

export const moveTask = async (
  id: string,
  data: ITaskMoveRequest
): Promise<{ task: ITask; warning?: string; incomplete_deps?: string[] }> => {
  return put<{ task: ITask; warning?: string; incomplete_deps?: string[] }>(
    `${BASE_URL}/${id}/move`,
    data
  );
};

export const reorderTasks = async (
  orders: ITaskOrder[]
): Promise<{ success: boolean; reordered: number }> => {
  return put<{ success: boolean; reordered: number }>(`${BASE_URL}/reorder`, orders);
};

export const checkDependencies = async (id: string): Promise<IDependencyCheck> => {
  return get<IDependencyCheck>(`${BASE_URL}/${id}/dependencies`);
};

// ============================================================================
// View-specific Queries
// ============================================================================

export const getTaskStats = async (filter?: {
  project_id?: string;
  task_owner?: string;
}): Promise<ITaskStats> => {
  const params: Record<string, string> = {};
  if (filter?.project_id) params.project_id = filter.project_id;
  if (filter?.task_owner) params.task_owner = filter.task_owner;

  const url = buildUrl(`${BASE_URL}/stats`, params);
  return get<ITaskStats>(url);
};

export const listTasksByOwner = async (
  owner: string,
  includeCompleted = true
): Promise<ITaskByOwnerResponse> => {
  const params = includeCompleted ? {} : { include_completed: 'false' };
  const url = buildUrl(`${BASE_URL}/by-owner/${encodeURIComponent(owner)}`, params);
  return get<ITaskByOwnerResponse>(url);
};

export const listTasksByProject = async (projectId: string): Promise<ITaskByProjectResponse> => {
  return get<ITaskByProjectResponse>(`${BASE_URL}/by-project/${projectId}`);
};

export const listTasksByPhase = async (
  projectId: string,
  phaseId: string
): Promise<ITaskByPhaseResponse> => {
  return get<ITaskByPhaseResponse>(`${BASE_URL}/by-phase/${projectId}/${phaseId}`);
};

// ============================================================================
// Helper Functions
// ============================================================================

export const groupTasksByStatus = (
  tasks: ITask[]
): { plan: ITask[]; doing: ITask[]; review: ITask[]; done: ITask[] } => {
  const grouped = {
    plan: [] as ITask[],
    doing: [] as ITask[],
    review: [] as ITask[],
    done: [] as ITask[],
  };

  tasks.forEach((task) => {
    if (grouped[task.status]) {
      grouped[task.status].push(task);
    }
  });

  // Sort each group by sort_order
  Object.keys(grouped).forEach((status) => {
    grouped[status as keyof typeof grouped].sort((a, b) => a.sort_order - b.sort_order);
  });

  return grouped;
};

export const groupTasksByPhase = (tasks: ITask[]): Record<string, ITask[]> => {
  const grouped: Record<string, ITask[]> = {};

  tasks.forEach((task) => {
    const phaseId = task.phase_id || 'unassigned';
    if (!grouped[phaseId]) {
      grouped[phaseId] = [];
    }
    grouped[phaseId].push(task);
  });

  // Sort tasks within each phase by sort_order
  Object.keys(grouped).forEach((phaseId) => {
    grouped[phaseId].sort((a, b) => a.sort_order - b.sort_order);
  });

  return grouped;
};

export const calculatePhaseProgress = (tasks: ITask[]): number => {
  if (tasks.length === 0) return 0;
  const completedTasks = tasks.filter((t) => t.status === 'done').length;
  return Math.round((completedTasks / tasks.length) * 100);
};
